import type { Link } from '@wix/public-schemas'
import { NEW_TAB_TARGET, USER_FILES_BASE_DOMAIN } from './constants'
import type { DocumentLinkData } from './linksDataTypes'
import { toQueryString } from './linkUtils'

export type DocumentLinkExtraData = {
	metaSiteId: string
	externalBaseUrl: string
	isPremiumDomain: boolean
}

const ALLOWED_FILE_EXTENSIONS_FROM_USER_DOMAIN = [
	'pdf',
	'docx',
	'zip',
	'pptx',
	'xlsx',
	'doc',
	'txt',
	'rar',
	'xls',
	'ppt',
	'ppsx',
	'epub',
	'rtf',
	'pub',
	'vcf',
	'csv',
]

const isFreeSite = (url: string) => {
	const hostname = new URL(url).hostname
	return hostname.endsWith('.wixsite.com') || hostname.endsWith('.editorx.io') || hostname.endsWith('.wixstudio.io')
}

const shouldServeFileFromUserDomain = (isPremiumDomain: boolean, externalBaseUrl: string, docId: string) => {
	const isPreview = new URL(externalBaseUrl).hostname.startsWith('editor.wix')
	if (!isPremiumDomain || isPreview || isFreeSite(externalBaseUrl)) {
		return false
	}

	return ALLOWED_FILE_EXTENSIONS_FROM_USER_DOMAIN.some((fileExtension) => docId.endsWith(fileExtension))
}

const getBaseUrlFilesPath = (externalBaseUrl: string) => {
	const externalBaseUrlWithTrailingSlash = externalBaseUrl.endsWith('/') ? externalBaseUrl : `${externalBaseUrl}/`
	return new URL('_files', externalBaseUrlWithTrailingSlash)
}

const getDocumentLinkSuffix = (docId: string, name: string, indexable: boolean) => {
	const isPDF = docId.endsWith('.pdf')
	if (isPDF) {
		return indexable ? '?index=true' : ''
	}

	return `?${toQueryString({ dn: name })}`
}

const resolveDocumentLink = (
	{ docId, name, indexable }: Omit<DocumentLinkData, 'type' | 'id'>,
	metaSiteId: string,
	externalBaseUrl: string,
	isPremiumDomain: boolean
): string => {
	const serveFileFromUserDomain = shouldServeFileFromUserDomain(isPremiumDomain, externalBaseUrl, docId)
	const baseUrl = serveFileFromUserDomain
		? getBaseUrlFilesPath(externalBaseUrl)
		: `https://${metaSiteId}.${USER_FILES_BASE_DOMAIN}`

	const prefixedDocId = docId.includes('/') ? docId : `ugd/${docId}`
	const prefixedDocIdWithSlash = prefixedDocId.startsWith('/') ? prefixedDocId : `/${prefixedDocId}`
	const suffix = getDocumentLinkSuffix(docId, name, indexable)
	return `${baseUrl}${prefixedDocIdWithSlash}${suffix}`
}

export const handleDocumentLink = (linkData: DocumentLinkData, extraData: DocumentLinkExtraData): Link => {
	const { metaSiteId, externalBaseUrl, isPremiumDomain } = extraData
	return {
		href: resolveDocumentLink(linkData, metaSiteId, externalBaseUrl, isPremiumDomain),
		target: NEW_TAB_TARGET,
	}
}
